import { useQuery } from '@tanstack/react-query'

import type { ChartType, TimePeriod } from '@/components/trader/PortfolioChart'
import { mockTokens } from '@/lib/data/mockTokens'
import { createMockQuery } from '@/lib/utils/mockQuery'

// Mark point interface
export interface MarkPoint {
  name: string
  type: 'signal' // Unified mark point type
  coord: [string, number]
  value: number
  timestamp: number
  symbol: string
  symbolSize: number
  tokenIcon: string
  tokenSymbol: string
  itemStyle: {
    color: string
    borderColor: string
    borderWidth: number
  }
  label: {
    show: boolean
    position: string
    formatter: string
    color: string
    fontSize: number
    fontWeight: string
  }
}

// Token selection utilities
function getRandomToken() {
  const randomIndex = Math.floor(Math.random() * mockTokens.length)
  return mockTokens[randomIndex]
}

function getTimestampByRandomTs(randomTs: number, timestamps: number[]): number {
  let index = 0
  let minDiff = Infinity
  for (const [i, timestamp] of timestamps.entries()) {
    const diff = Math.abs(timestamp - randomTs)
    if (diff < minDiff) {
      index = i
      minDiff = diff
    }
  }
  return timestamps[index]
}

// Timestamp utilities for realistic simulation
function generateRandomTimestamp(baseTimestamp: number, maxDeltaMinutes = 30): number {
  // Generate random delta in milliseconds (± maxDeltaMinutes)
  const maxDeltaMs = maxDeltaMinutes * 60 * 1000
  const randomDelta = (Math.random() - 0.5) * 2 * maxDeltaMs
  return Math.floor(baseTimestamp + randomDelta)
}

// Unified mark point creation
function createSignalMarkPoint(
  value: number,
  index: number,
  timestamp: number,
  timestamps: number[],
): MarkPoint {
  const token = getRandomToken()
  // Generate realistic timestamp variation (within ±15 minutes)
  const randomizedTimestamp = generateRandomTimestamp(timestamp, 15)
  const ts = getTimestampByRandomTs(randomizedTimestamp, timestamps)

  return {
    name: `Signal ${value.toFixed(2)}`,
    type: 'signal',
    coord: [`${ts}`, value],
    value,
    timestamp: ts,
    symbol: `image://${token.icon}`,
    symbolSize: 24,
    tokenIcon: token.icon,
    tokenSymbol: token.symbol,
    itemStyle: {
      color: '#BCFE2D',
      borderColor: '#BCFE2D',
      borderWidth: 2,
    },
    label: {
      show: false,
      position: 'top',
      formatter: `${token.symbol} $${value.toFixed(2)}`,
      color: '#BCFE2D',
      fontSize: 10,
      fontWeight: 'bold',
    },
  }
}

// Unified signal detection algorithm
function generateSignalPoints(values: number[], timestamps: number[]): MarkPoint[] {
  if (values.length < 3)
    return []

  const markPoints: MarkPoint[] = []

  // Generate random mark points at interesting data points
  // Sample a few points across the dataset for realistic distribution
  // const sampleSize = Math.min(16, Math.floor(values.length / 10)) // Sample 10% of points, max 8
  const sampleSize = 8
  const step = Math.floor(values.length / sampleSize)

  for (let i = 0; i < sampleSize; i++) {
    const index = Math.min(step * i + Math.floor(Math.random() * step), values.length - 1)
    const value = values[index]
    const timestamp = timestamps[index]

    markPoints.push(createSignalMarkPoint(value, index, timestamp, timestamps))
  }

  return markPoints
}

// Main mark points generation function
function generateMarkPoints(timestamps: number[], values: number[]): MarkPoint[] {
  if (values.length < 3)
    return []

  // Generate unified signal points
  const allMarkPoints = generateSignalPoints(values, timestamps)

  // Process timestamp grouping to handle realistic timing variations
  return allMarkPoints
}

// Hook interface
interface UseChartMarkPointsOptions {
  timestamps: number[]
  values: number[]
  activePeriod: TimePeriod
  activeChart: ChartType
  enabled?: boolean
}

// Main hook
export function useChartMarkPoints({
  timestamps,
  values,
  activePeriod,
  activeChart,
  enabled = true,
}: UseChartMarkPointsOptions) {
  // Create a stable key for the query
  const queryKey = [
    'chartMarkPoints',
    activePeriod,
    activeChart,
    timestamps.length,
    values.length,
    timestamps[0],
    values[0],
  ]

  return useQuery({
    queryKey,
    queryFn: () => createMockQuery(generateMarkPoints(timestamps, values)),
    enabled: enabled && timestamps.length > 0 && values.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}
